/**
 * Invite link/code extraction for the zero-club Welcome paste box (#2323).
 *
 * Accepts whatever a user pastes or types — a full invite URL, a relative
 * path, or a bare code — and returns the canonical code segment for
 * navigation to `/invite/:code`, or null when nothing code-shaped is present.
 *
 * Existence/expiry validation is the SERVER's job (InviteAcceptPage fetches
 * the code and renders its own error state) — this function only gates
 * plausibility so obvious garbage gets an inline error instead of a
 * navigation to a guaranteed 404.
 *
 * Code format (backend invites.ts generateInviteCode): 8 chars from an
 * unambiguous UPPERCASE charset (no 0/O/I/1). Bare typed input matching that
 * charset case-insensitively is uppercased (humans transcribe lowercase;
 * lookups are case-sensitive). Codes extracted from URLs pass through as-is
 * (share links already carry canonical casing), and the generic-shape
 * fallback (4-64 alphanumerics) is deliberately looser than today's
 * generator so a future code format doesn't hard-fail this parser.
 */

/** Today's generator alphabet, case-insensitive form. */
const KNOWN_CODE_SHAPE = /^[a-hj-np-z2-9]{8}$/i;

/** Lenient plausibility gate for any code-shaped token. */
const GENERIC_CODE_SHAPE = /^[A-Za-z0-9]{4,64}$/;

/** Matches the code segment of an invite URL or path, e.g.
 *  https://oro.fit/invite/ABCD2345?utm=x or /invite/ABCD2345 */
const INVITE_PATH = /\/invite\/([^/?#\s]+)/i;

export function extractInviteCode(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const pathMatch = trimmed.match(INVITE_PATH);
  if (pathMatch) {
    // Strip trailing punctuation a human adds when forwarding a link in
    // prose ("Join here: https://oro.fit/invite/ABCD2345."). Leading junk
    // can't occur — the capture starts right after "/invite/".
    const candidate = pathMatch[1].replace(/[^A-Za-z0-9]+$/, '');
    return GENERIC_CODE_SHAPE.test(candidate) ? candidate : null;
  }

  // Bare input: reject anything with whitespace or URL-ish structure — a
  // pasted non-invite URL must not be mistaken for a code.
  if (/[\s/:?#@]/.test(trimmed)) return null;

  if (KNOWN_CODE_SHAPE.test(trimmed)) return trimmed.toUpperCase();
  return GENERIC_CODE_SHAPE.test(trimmed) ? trimmed : null;
}
